//* Import libraries
import { useState, useCallback, useEffect } from "react";

//* Import react-router hooks
import { useNavigate } from "react-router-dom";

//* Import components
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Import custom hooks
import { useDiagrams } from "../../../hooks//diagrams/useDiagrams";

//* Import Auth
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import types
type Diagram = {
  id: string;
  pageTitle: string;
  testName: string;
  createdAt: string;
  nicknameLayout: string;
};

var debounce = require("lodash.debounce");

export function DiagramView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [setMe] = useState<any | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [diagramToDelete, setDiagramToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalDiagrams, setTotalDiagrams] = useState<number>(0);

  const {
    diagrams,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteDiagram,
    refetch,
    changePage,
    isDeleting,
  } = useDiagrams(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token, setMe]);

  useEffect(() => {
    setTotalDiagrams(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (diagramToDelete) {
      try {
        const currentPageItems = diagrams.length;
        const currentPage = pagination.currentPage;

        await deleteDiagram(diagramToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setDiagramToDelete(null);
      } catch (error) {
        console.error("Error deleting diagram:", error);
      }
    }
  }, [
    diagramToDelete,
    deleteDiagram,
    diagrams.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setDiagramToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  if (isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        search_text="Pesquise pelo nome da diagramação"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Diagramas (Total: ${totalDiagrams})`}
        not_found_message="Nenhum diagrama encontrado"
        button_link="/layouts_default_exams/create"
        button_text="Adicionar diagrama"
        deleting_message="Ao deletar este diagrama, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) =>
          navigate(`/layouts_default_exams/edit/${id}`)
        }
        onChangePagination={handleChangePagination}
        onManagerActionClick={(value) => {
          navigate(`/diagrams/view/${value}`, {
            state: { filter: value, key: "diagram_id" },
          });
        }}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={diagrams.map((diagram: Diagram) => ({
          ...diagram,
          key: diagram.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "nicknameLayout", type: "text", header: "Nome do Diagrama" },
          { name: "pageTitle", type: "text", header: "Título da Página" },
          { name: "testName", type: "text", header: "Nome do Teste" },
          { name: "createdAt", type: "date", header: "Data de Criação" },
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
